import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Grid,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Divider,
  Dialog,
  IconButton,
  useTheme,
  useMediaQuery,
  Rating,
} from '@mui/material';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import CloseIcon from '@mui/icons-material/Close';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import { useAccommodationBySlug } from '../hooks/useCMS';
import SafeImage from '../components/SafeImage';
import CountryFlag from '../components/CountryFlag';
import { AccommodationReview } from '../types/cms';
import SEOHead from '../components/SEOHead';
import { getCanonicalUrl, SITE_CONFIG } from '../lib/seo';
import { useWhatsApp } from '../contexts/WhatsAppContext';

function AlojamientoDetalle() {
  const { slug } = useParams<{ slug: string }>();
  const { data, loading, error } = useAccommodationBySlug(slug || '');
  const accommodation = data?.accommodation;
  const [selectedImageIndex, setSelectedImageIndex] = useState<number | null>(null);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const modalBoxRef = useRef<HTMLDivElement>(null);
  const { setMessage, clearMessage } = useWhatsApp();

  // Armar mensaje de WhatsApp - calcular siempre, incluso durante loading
  const whatsappMsg = accommodation ? `Hola! Quiero consultar por el alojamiento ${accommodation.name}` : null;

  // Enfocar el modal cuando se abre para que los eventos de teclado funcionen
  useEffect(() => {
    if (selectedImageIndex !== null && modalBoxRef.current) {
      modalBoxRef.current.focus();
    }
  }, [selectedImageIndex]);

  // Actualizar el mensaje en el contexto cuando cambie el alojamiento
  useEffect(() => {
    if (accommodation && whatsappMsg) {
      setMessage(whatsappMsg);
    }
    // Limpiar el mensaje cuando se desmonte el componente
    return () => {
      clearMessage();
    };
  }, [accommodation, whatsappMsg, setMessage, clearMessage]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error || !accommodation) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">
          No se pudo cargar el alojamiento. Intenta de nuevo más tarde.
        </Alert>
      </Container>
    );
  }
  
  const whatsappUrl = `https://wa.me/59898885151?text=${encodeURIComponent(whatsappMsg || '')}`;

  // Función para obtener el texto de la categoría
  const getCategoryLabel = (category?: string) => {
    const labels: Record<string, string> = {
      'basico': 'Básico',
      'medio': 'Medio',
      'superior': 'Superior',
    };
    return labels[category || ''] || 'Sin categoría';
  };

  // Función para obtener el color de la categoría
  const getCategoryColor = (category?: string) => {
    const colors: Record<string, 'default' | 'primary' | 'secondary'> = {
      'basico': 'default',
      'medio': 'primary',
      'superior': 'secondary',
    };
    return colors[category || ''] || 'default';
  };

  // Obtener todas las imágenes (cover + galería)
  const allImages = [
    ...(accommodation.coverImage ? [{ image: accommodation.coverImage, altText: accommodation.name }] : []),
    ...(accommodation.galleryImages || []),
  ];

  // Funciones para el modal de galería
  const handleOpenModal = (index: number) => {
    setSelectedImageIndex(index);
  };

  const handleCloseModal = () => {
    setSelectedImageIndex(null);
  };

  const handlePrevious = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (selectedImageIndex !== null && allImages.length > 0) {
      const newIndex = selectedImageIndex > 0 ? selectedImageIndex - 1 : allImages.length - 1;
      setSelectedImageIndex(newIndex);
    }
  };

  const handleNext = (e?: React.MouseEvent) => {
    e?.stopPropagation();
    e?.preventDefault();
    if (selectedImageIndex !== null && allImages.length > 0) {
      const newIndex = selectedImageIndex < allImages.length - 1 ? selectedImageIndex + 1 : 0;
      setSelectedImageIndex(newIndex);
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowLeft') {
      handlePrevious();
    } else if (event.key === 'ArrowRight') {
      handleNext();
    } else if (event.key === 'Escape') {
      handleCloseModal();
    }
  };

  // Parsear reviews si es un string JSON
  let reviews: AccommodationReview[] = [];
  if (accommodation.reviews) {
    try {
      reviews = typeof accommodation.reviews === 'string' 
        ? JSON.parse(accommodation.reviews) 
        : accommodation.reviews;
    } catch (e) {
      console.error('Error parsing reviews:', e);
    }
  }

  // Calcular rating promedio
  const averageRating = reviews.length > 0
    ? reviews.reduce((sum, review) => sum + (review.rating || 0), 0) / reviews.length
    : 0;

  // Generar datos SEO - siempre renderizar, incluso durante loading
  const seoTitle = accommodation
    ? `${accommodation.name} en ${accommodation.destination?.title || ''}`
    : undefined;
  
  const seoDescription = accommodation
    ? `${accommodation.description || ''} Categoría ${getCategoryLabel(accommodation.category)}. Ubicado en ${accommodation.destination?.title || ''}, ${accommodation.destination?.country?.name || ''}. ${averageRating > 0 ? `Rating: ${averageRating.toFixed(1)}/5.` : ''}`
    : undefined;
  
  const seoKeywords = accommodation
    ? [
        accommodation.name,
        `alojamiento en ${accommodation.destination?.title || ''}`,
        `hotel ${getCategoryLabel(accommodation.category).toLowerCase()}`,
        ...(accommodation.destination?.country ? [`alojamiento ${accommodation.destination.country.name}`] : []),
        'hotel',
        'posada',
        'alojamiento turístico',
      ]
    : undefined;
  
  const seoImage = accommodation?.coverImage?.publicUrl || accommodation?.thumbnail?.publicUrl;
  // Usar el slug de la URL actual si no hay alojamiento aún
  const seoUrl = accommodation ? `/alojamientos/${accommodation.slug}` : (slug ? `/alojamientos/${slug}` : '/alojamientos');

  // Schema JSON-LD para LodgingBusiness
  const lodgingSchema = accommodation ? {
    '@context': 'https://schema.org',
    '@type': 'LodgingBusiness',
    name: accommodation.name,
    description: accommodation.description,
    image: accommodation.coverImage?.publicUrl || accommodation.thumbnail?.publicUrl || `${SITE_CONFIG.url}${SITE_CONFIG.defaultOGImage}`,
    url: getCanonicalUrl(`/alojamientos/${accommodation.slug}`),
    ...(accommodation.location?.lat && accommodation.location?.lng && {
      geo: {
        '@type': 'GeoCoordinates',
        latitude: typeof accommodation.location.lat === 'string' 
          ? parseFloat(accommodation.location.lat) 
          : accommodation.location.lat,
        longitude: typeof accommodation.location.lng === 'string'
          ? parseFloat(accommodation.location.lng)
          : accommodation.location.lng,
      },
    }),
    ...(accommodation.location?.address && {
      address: {
        '@type': 'PostalAddress',
        streetAddress: accommodation.location.address,
      },
    }),
    ...(averageRating > 0 && {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: averageRating.toFixed(1),
        reviewCount: reviews.length,
      },
    }),
    ...(accommodation.destination && {
      containedInPlace: {
        '@type': 'Place',
        name: accommodation.destination.title,
        ...(accommodation.destination.country && {
          addressCountry: accommodation.destination.country.name,
        }),
      },
    }),
  } : undefined;

  return (
    <>
      <SEOHead
        key={accommodation?.id || slug} // Forzar re-render cuando cambian los datos
        title={seoTitle}
        description={seoDescription}
        keywords={seoKeywords}
        image={seoImage}
        url={seoUrl}
        type="article"
        schema={lodgingSchema}
      />
      <Container maxWidth="md" sx={{ mt: 4 }}>
      <Button component={Link} to="/alojamientos" variant="outlined" sx={{ mb: 2 }}>
        ← Volver a alojamientos
      </Button>
      <Grid container spacing={4}>
        {/* Imagen principal */}
        <Grid item xs={12} md={6}>
          <Card>
            <SafeImage
              src={accommodation.coverImage?.publicUrl || accommodation.thumbnail?.publicUrl}
              alt={accommodation.name}
              height={320}
              sx={{ width: '100%', objectFit: 'cover', cursor: allImages.length > 1 ? 'pointer' : 'default' }}
              onClick={() => allImages.length > 1 && handleOpenModal(0)}
            />
          </Card>
        </Grid>
        {/* Info principal */}
        <Grid item xs={12} md={6}>
          <Typography variant="h4" gutterBottom>{accommodation.name}</Typography>
          
          {/* Categoría y Rating */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {accommodation.category && (
              <Chip
                label={getCategoryLabel(accommodation.category)}
                color={getCategoryColor(accommodation.category)}
              />
            )}
            {averageRating > 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Rating value={averageRating} precision={0.5} readOnly />
                <Typography variant="body2" color="text.secondary">
                  ({reviews.length} {reviews.length === 1 ? 'reseña' : 'reseñas'})
                </Typography>
              </Box>
            )}
          </Box>

          {/* Ubicación */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            {accommodation.destination?.country?.bandera && (
              <CountryFlag
                src={accommodation.destination.country.bandera.publicUrl}
                alt={accommodation.destination.country.name || 'Bandera del país'}
                width={24}
                height={16}
                sx={{ mr: 1 }}
              />
            )}
            <LocationOnIcon sx={{ mr: 0.5, color: 'text.secondary' }} fontSize="small" />
            <Typography variant="body1" color="text.secondary">
              {accommodation.destination?.title}
              {accommodation.destination?.country?.name && `, ${accommodation.destination.country.name}`}
            </Typography>
          </Box>

          {/* Dirección si existe */}
          {accommodation.location?.address && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {accommodation.location.address}
            </Typography>
          )}

          <Typography variant="body1" sx={{ mb: 2 }}>{accommodation.description}</Typography>
          
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
            <Button
              variant="contained"
              color="success"
              startIcon={<WhatsAppIcon />}
              href={whatsappUrl}
              target="_blank"
            >
              Consultar por WhatsApp
            </Button>
            {accommodation.destination && (
              <Button
                variant="outlined"
                color="primary"
                component={Link}
                to={`/destinos/${accommodation.destination.slug}`}
              >
                Ver destino
              </Button>
            )}
          </Box>
        </Grid>
      </Grid>

      {/* Mapa de ubicación */}
      {accommodation.location?.lat && accommodation.location?.lng && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Ubicación</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <iframe
              src={`https://maps.google.com/maps?q=${accommodation.location.lat},${accommodation.location.lng}&hl=es&z=15&output=embed`}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title={`Ubicación de ${accommodation.name}`}
            />
          </Box>
          {accommodation.location.address && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              {accommodation.location.address}
            </Typography>
          )}
          <Button
            variant="outlined"
            size="small"
            href={`https://www.google.com/maps/dir/?api=1&destination=${accommodation.location.lat},${accommodation.location.lng}`}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ mt: 1 }}
          >
            Abrir en Google Maps
          </Button>
        </Box>
      )}

      {/* Video */}
      {accommodation.videoUrl && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Video</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              position: 'relative',
              paddingBottom: '56.25%', // 16:9 aspect ratio
              height: 0,
              overflow: 'hidden',
              borderRadius: 2,
              boxShadow: 2,
            }}
          >
            <iframe
              src={accommodation.videoUrl}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                width: '100%',
                height: '100%',
                border: 0,
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={`Video de ${accommodation.name}`}
            />
          </Box>
        </Box>
      )}

      {/* Galería de imágenes */}
      {accommodation.galleryImages && accommodation.galleryImages.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Galería</Typography>
          <Divider sx={{ mb: 2 }} />
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              pb: 1,
              scrollbarWidth: 'thin',
              '&::-webkit-scrollbar': {
                height: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'rgba(0,0,0,0.1)',
                borderRadius: 4,
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'rgba(0,0,0,0.3)',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.5)',
                },
              },
            }}
          >
            {accommodation.galleryImages.map((img, index) => (
              <Card
                key={img.id}
                sx={{
                  minWidth: 200,
                  cursor: 'pointer',
                  transition: 'transform 0.2s, box-shadow 0.2s',
                  '&:hover': {
                    transform: 'scale(1.05)',
                    boxShadow: 4,
                  },
                }}
                onClick={() => handleOpenModal(index + (accommodation.coverImage ? 1 : 0))}
              >
                <SafeImage
                  src={img.image?.publicUrl}
                  alt={img.altText || `Imagen del alojamiento ${accommodation.name}`}
                  height={150}
                  sx={{ width: 200, objectFit: 'cover' }}
                />
              </Card>
            ))}
          </Box>
        </Box>
      )}

      {/* Modal de galería */}
      {selectedImageIndex !== null && allImages.length > 0 && (
        <Dialog
          open={selectedImageIndex !== null}
          onClose={handleCloseModal}
          maxWidth="lg"
          fullWidth
          fullScreen={fullScreen}
          PaperProps={{
            sx: {
              backgroundColor: 'rgba(0, 0, 0, 0.9)',
              boxShadow: 'none',
            },
          }}
        >
          <Box
            ref={modalBoxRef}
            sx={{
              position: 'relative',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: '80vh',
              p: { xs: 2, md: 4 },
              outline: 'none',
            }}
            tabIndex={-1}
            onKeyDown={handleKeyDown}
          >
            {/* Botón cerrar */}
            <IconButton
              onClick={handleCloseModal}
              sx={{
                position: 'absolute',
                top: 16,
                right: 16,
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                '&:hover': {
                  backgroundColor: 'rgba(0, 0, 0, 0.7)',
                },
                zIndex: 2,
              }}
            >
              <CloseIcon />
            </IconButton>

            {/* Botón anterior */}
            {allImages.length > 1 && (
              <IconButton
                onClick={handlePrevious}
                sx={{
                  position: 'absolute',
                  left: 16,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 2,
                }}
              >
                <ChevronLeftIcon fontSize="large" />
              </IconButton>
            )}

            {/* Imagen */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '100%',
                maxHeight: '90vh',
              }}
            >
              <SafeImage
                key={`gallery-${selectedImageIndex}`}
                src={allImages[selectedImageIndex].image?.publicUrl}
                alt={allImages[selectedImageIndex].altText || `Imagen ${selectedImageIndex + 1} del alojamiento ${accommodation.name}`}
                sx={{
                  maxWidth: '100%',
                  maxHeight: '90vh',
                  objectFit: 'contain',
                }}
              />
            </Box>

            {/* Botón siguiente */}
            {allImages.length > 1 && (
              <IconButton
                onClick={handleNext}
                sx={{
                  position: 'absolute',
                  right: 16,
                  color: 'white',
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.7)',
                  },
                  zIndex: 2,
                }}
              >
                <ChevronRightIcon fontSize="large" />
              </IconButton>
            )}

            {/* Contador de imágenes */}
            <Box
              sx={{
                position: 'absolute',
                bottom: 16,
                left: '50%',
                transform: 'translateX(-50%)',
                color: 'white',
                backgroundColor: 'rgba(0, 0, 0, 0.5)',
                px: 2,
                py: 1,
                borderRadius: 2,
                zIndex: 2,
              }}
            >
              <Typography variant="body2">
                {selectedImageIndex + 1} / {allImages.length}
              </Typography>
            </Box>
          </Box>
        </Dialog>
      )}

      {/* Reviews */}
      {reviews.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Reseñas</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {reviews.map((review, index) => (
              <Grid item xs={12} key={index}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {review.author}
                      </Typography>
                      <Rating value={review.rating} readOnly size="small" />
                    </Box>
                    {review.date && (
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                        {new Date(review.date).toLocaleDateString('es-ES', { 
                          day: 'numeric', 
                          month: 'long', 
                          year: 'numeric' 
                        })}
                      </Typography>
                    )}
                    <Typography variant="body2">{review.comment}</Typography>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}

      {/* Paquetes relacionados */}
      {accommodation.packages && accommodation.packages.length > 0 && (
        <Box sx={{ mt: 4 }}>
          <Typography variant="h6" gutterBottom>Paquetes que incluyen este alojamiento</Typography>
          <Divider sx={{ mb: 2 }} />
          <Grid container spacing={2}>
            {accommodation.packages.map(pkg => (
              <Grid item xs={12} sm={6} md={4} key={pkg.id}>
                <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <SafeImage
                    src={pkg.imageCover?.publicUrl}
                    alt={pkg.title}
                    height={150}
                    sx={{ width: '100%', objectFit: 'cover' }}
                  />
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography gutterBottom variant="subtitle1" component="h3">
                      {pkg.title}
                    </Typography>
                    {pkg.priceFrom && (
                      <Typography variant="h6" color="primary" sx={{ mb: 2 }}>
                        Desde ${pkg.priceFrom.toLocaleString().replace(/,/g, '.')}
                      </Typography>
                    )}
                    <Button
                      variant="contained"
                      color="primary"
                      fullWidth
                      component={Link}
                      to={`/paquetes/${pkg.slug}`}
                    >
                      Ver paquete
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Box>
      )}
    </Container>
    </>
  );
}

export default AlojamientoDetalle;

